import { useVirtualizer } from '@tanstack/react-virtual'
import { useCallback, useMemo, useRef } from 'react'

export const useTableVirtualizer = (
	rowCount: number,
	rowHeight: number,
	overscan: number,
) => {
	const parentRef = useRef<HTMLDivElement>(null)

	const measureElement = useCallback(
		(el: Element | null) => {
			if (!el) return rowHeight
			const rect = el.getBoundingClientRect()
			return rect.height > 0 ? rect.height : rowHeight
		},
		[rowHeight],
	)

	const virtualizer = useVirtualizer({
		count: rowCount,
		getScrollElement: () => parentRef.current,
		estimateSize: (_index) => {
			return rowHeight
		},
		overscan,
		measureElement,
	})

	const virtualItems = virtualizer.getVirtualItems()

	const offsets = useMemo(() => {
		const topOffset = virtualItems?.[0]?.start ?? 0
		const lastVirtualItem = virtualItems?.[virtualItems.length - 1]
		const lastItemIndex = lastVirtualItem?.index ?? -1

		const isLastItemRendered = lastItemIndex === rowCount - 1

		let bottomOffset = virtualizer.getTotalSize() - (lastVirtualItem?.end ?? 0)

		if (isLastItemRendered && bottomOffset < 10) {
			bottomOffset = 0
		}

		return { topOffset, bottomOffset }
	}, [virtualItems, virtualizer, rowCount])

	return {
		parentRef,
		virtualizer,
		virtualItems,
		topOffset: offsets.topOffset,
		bottomOffset: offsets.bottomOffset,
	}
}
